import React from 'react';
import {
  MessageSquare, Lightbulb, PenTool, Code2, TestTube, Rocket, ArrowRight,
} from 'lucide-react';

const steps = [
  {
    icon: MessageSquare,
    title: 'Discovery & Consultation',
    description: 'We begin by understanding your vision, goals, and requirements through detailed consultations.',
    activities: ['Requirements gathering', 'Stakeholder interviews', 'Market analysis', 'Technical feasibility study'],
  },
  {
    icon: Lightbulb,
    title: 'Strategy & Planning',
    description: 'Our team develops a comprehensive roadmap and technical architecture for your project.',
    activities: ['Project roadmap creation', 'Technology stack selection', 'Timeline & milestone planning', 'Resource allocation'],
  },
  {
    icon: PenTool,
    title: 'Design & Prototyping',
    description: 'We create intuitive designs and interactive prototypes to visualize the final product.',
    activities: ['UI/UX design', 'Wireframing', 'Interactive prototypes', 'Design system creation'],
  },
  {
    icon: Code2,
    title: 'Development',
    description: 'Our developers bring your application to life using agile methodologies and best practices.',
    activities: ['Sprint planning', 'Code development', 'API integration', 'Database design'],
  },
  {
    icon: TestTube,
    title: 'Testing & Quality Assurance',
    description: 'Rigorous testing ensures your application is bug-free, secure, and performs optimally.',
    activities: ['Unit testing', 'Integration testing', 'Performance testing', 'Security testing'],
  },
  {
    icon: Rocket,
    title: 'Deployment & Support',
    description: 'We handle the launch and provide ongoing support to ensure continued success.',
    activities: ['Deployment setup', 'Production launch', 'Monitoring & maintenance', 'Continuous improvements'],
  },
];

const Process = () => {
  return (
    <section id="process" className="relative py-24 md:py-32 bg-paper-3/40 border-y border-rule-soft">
      <div className="frame">
        {/* Masthead */}
        <header className="grid grid-cols-12 gap-x-6 lg:gap-x-10 items-end pb-6 border-b border-ink">
          <div className="col-span-12 lg:col-span-5 flex items-center gap-3">
            <span className="section-num">05 / Process</span>
            <span className="label-muted">How we work</span>
          </div>
          <div className="col-span-12 lg:col-span-7 mt-6 lg:mt-0">
            <h2 className="display-lg">
              Our Development <span className="text-accent">Process</span>
            </h2>
            <p className="mt-5 max-w-measure text-ink-soft leading-relaxed">
              A proven methodology that ensures successful project delivery from
              concept to deployment.
            </p>
          </div>
        </header>

        {/* Process grid */}
        <ol className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule-soft border border-rule-soft">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={s.title} className="group bg-paper-2 p-6 md:p-7 relative">
                <div className="flex items-center justify-between mb-6">
                  <span className="chip-lime">STEP {String(i + 1).padStart(2, '0')}</span>
                  <div className="w-9 h-9 flex items-center justify-center border border-ink bg-paper">
                    <Icon className="w-4 h-4 text-ink" strokeWidth={1.5} />
                  </div>
                </div>

                <h3 className="font-display font-semibold text-xl md:text-[1.4rem] tracking-tight text-ink leading-tight">
                  {s.title}
                </h3>

                <p className="mt-3 text-ink-soft text-[15px] leading-relaxed">
                  {s.description}
                </p>

                <ul className="mt-5 pt-5 border-t border-rule-soft space-y-2">
                  {s.activities.map((a) => (
                    <li key={a} className="flex items-baseline gap-2.5 font-mono text-[12px] text-ink-soft">
                      <ArrowRight className="w-3 h-3 text-accent flex-shrink-0 translate-y-[1px]" strokeWidth={2} />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>

                {/* Arrow next */}
                {i < steps.length - 1 && (
                  <span
                    aria-hidden
                    className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center bg-paper-2 border border-rule-soft text-muted z-10"
                  >
                    →
                  </span>
                )}
              </li>
            );
          })}
        </ol>

        {/* CTA */}
        <div className="mt-12 grid grid-cols-12 gap-x-6 lg:gap-x-10 items-center">
          <p className="col-span-12 lg:col-span-7 text-ink-soft max-w-measure">
            Ready to start your project? Let's discuss how we can help bring your vision to life.
          </p>
          <div className="col-span-12 lg:col-span-5 mt-5 lg:mt-0 lg:text-right">
            <button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="btn-ink"
            >
              <span>Start Your Project</span>
              <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
